import { useContext, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { AppContext } from "../../../context/AppContext"
import ConfigurationContext from "../../../context/ConfigurationContext"
import VariableClass from "../../../helpers-shared/configuration/variable"
import AreaClass from "../../../helpers/configuration/area"
import { findArea } from "../../../helpers/configuration/areas"
import {
  calculateIncompatibilities,
  calculateVariableIncompatibilities,
} from "../../../helpers/configuration/incompatibilities"
import { getCmsAssetsUrl } from "../../../helpers/general"
import useScroll from "../../../hooks/use-Scroll"
import { getSelectedModel } from "../../../hooks/use-selected-model"
import Accordion from "../../Accordion/Accordion"
import IconInfo from "../../IconInfo/IconInfo"
import Button from "../../buttons/Button"
import AllWallsSameControl from "../AllWallsSameControl/AllWallsSameControl"
import AreaHeader from "../AreaHeader/AreaHeader"
import Booleano from "../fields/Booleano/Booleano"
import MuestraCircular from "../fields/MuestraCircular/MuestraCircular"
import MuestraCuadrada from "../fields/MuestraCuadrada/MuestraCuadrada"
import MuestraRectangular from "../fields/MuestraRectangular/MuestraRectangular"
import OpcionesIcono from "../fields/OpcionesIcono/OpcionesIcono"
import OpcionesInline from "../fields/OpcionesInline/OpcionesInline"
import Switch from "../fields/Switch/Switch"

import style from "./Area.module.scss"

const assetsUrl = getCmsAssetsUrl()

const Area = ({ id }) => {
  const scrollContainerRef = useRef(null);

  const { i18n, t } = useTranslation()
  const navigate = useNavigate()

  const context = useContext(ConfigurationContext)
  const appContext = useContext(AppContext)

  const { store } = appContext
  const { nextArea, goToArea, allAreas, values } = context

  const handleGoToNext = () => {
    goToArea(nextArea)
  }
  const handleGoToSummary = () => {
    navigate(`/${i18n.language}/configure/${getSelectedModel()}/summary`)
  }
  const y = useScroll()
  document.documentElement.style.setProperty("--scroll", `${y}px`)

  const renderArea = (areaObj, level = 0) => {
    const hijos = areaObj.getHijos()
    const info = areaObj.getInfo(i18n.language)
    const description = areaObj.getDescription(i18n.language)

    if (!hijos || hijos.length <= 0) {
      const displayInTab = areaObj.displayInTab()
      const areaName = areaObj.getName(i18n.language)
      const showTitle = level > 0

      if (displayInTab) {
        return (
          <Accordion
            key={`subarea-${areaObj.getId()}`}
            id={`subarea-${areaObj.getId()}`}
            title={areaName}
          >
            {renderVariables(areaObj)}
            {info && renderInfo(info)}
          </Accordion>
        )
      }

      let selectedText = areaObj.getConectorSelectionText(context, i18n.language)
      
      return (
        <div className={style.subarea} key={`subarea-${areaObj.getId()}`}>
          <div className={style.subareaHeader}>  
            {showTitle && <h2>{areaName}</h2>}
            {description && <IconInfo info={description} title={areaName} />}
          </div>
          {renderVariables(areaObj)}
          {selectedText && (
            <div className={style.selectedOptionText}>
              <span className={style.selectedOptionTextTitle}>{t('selected')}:</span> {selectedText}
            </div>
          )}
          {info && renderInfo(info)}
        </div>
      )
    }

    return hijos.map(hijo => {
      const [hijoId, hijoData] = hijo

      const hijoObj = AreaClass(hijoId, hijoData)

      return renderArea(hijoObj, level + 1)
    })
  }

  const renderInfo = info => {
    return <div className={style.infoText}>{info}</div>
  }

  const renderVariables = areaObj => {
    const variables = areaObj.getVariables()

    return Object.entries(variables).map(variable => renderVariable(variable))
  }

  const renderVariable = variable => {
    if (variable?.[0] === "EV_INSTALACION") {
      const pais = context?.values?.EV_PAIS;
      const idioma = i18n?.language;

      const textoFuente = variable?.[1]?.VALORES?.MONOFASICO?.TEXTO?.[pais]?.[idioma];

      if (textoFuente !== undefined) {
        if (!variable?.[1]?.VALORES?.MONOFASICO?.TEXTO) {
          variable[1].VALORES.MONOFASICO.TEXTO = {};
        }
        variable[1].VALORES.MONOFASICO.TEXTO[idioma] = textoFuente;
      }
    }

    const [variableId, variableData] = variable

    const variableObj = VariableClass(variableId, variableData)

    const variableName = variableObj.getName(i18n.language)
    const displayInTab = variableObj.displayInTab()
    const variableIsActive = variableObj.isActive()

    if (!variableIsActive) {
      return ""
    }

    if (displayInTab) {
      return (
        <Accordion
          key={`variable-${variableId}`}
          id={`variable-${variableId}`}
          title={variableName}
        >
          <div className={style.variable}>
            {renderVariableOptions(variableObj)}
          </div>
        </Accordion>
      )
    }

    return (
      <div className={style.variable} key={`variable-${variableId}`}>
        {renderVariableOptions(variableObj)}
      </div>
    )
  }

  const renderVariableOptions = variableObj => {
    const variableId = variableObj.getId()
    const variableName = variableObj.getName(i18n.language)
    const displayLabel = variableObj.displayLabel()
    const isMultiselect = variableObj.isMultiselect()
    const visualizacion = variableObj.getVisualizacion()
    const isIncluded = variableObj.isIncluded()
    const columns = variableObj.getColumns()
    const variablePreparedOptions = variableObj.getPreparedOptions(
      i18n.language,
      { assetsUrl }
    )

    if (!visualizacion) {
      return ""
    }

    let variableOptions
    let preparedOptions
    if (visualizacion === "BOOLEANO") {
      variableOptions = calculateVariableIncompatibilities(
        values,
        variableId,
        store.incompatibilities
      )
    } else {
      preparedOptions = calculateIncompatibilities(
        values,
        variableId,
        variablePreparedOptions,
        store.incompatibilities
      )
    }

    variableOptions = { ...variableOptions, included: isIncluded }

    switch (visualizacion) {
      case "OPCIONES_ICONO":
        return (
          <OpcionesIcono
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
            multiselect={isMultiselect}
          />
        )
      case "MUESTRA_CIRCULAR":
        return (
          <MuestraCircular
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
            multiselect={isMultiselect}
          />
        )
      case "MUESTRA_CUADRADA":
        return (
          <MuestraCuadrada
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
            multiselect={isMultiselect}
            columns={columns}
          />
        )
      case "MUESTRA_RECTANGULAR":
        return (
          <MuestraRectangular
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
            multiselect={isMultiselect}
            columns={columns}
          />
        )
      case "SWITCH":
        return (
          <Switch
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
          />
        )
      case "OPCIONES_INLINE":
        return (
          <OpcionesInline
            title={displayLabel ? variableName : ""}
            name={variableId}
            options={preparedOptions}
            multiselect={isMultiselect}
          />
        )
      case "BOOLEANO":
        return (
          <Booleano
            label={variableName}
            name={variableId}
            {...variableOptions}
          />
        )
      default:
        return ""
    }
  }

  // Data for the next area
  const nextAreaData = findArea(allAreas, nextArea)
  const nextAreaObj = AreaClass(nextArea, nextAreaData)
  const nextName = nextAreaObj.getName(i18n.language)

  // Data for the current area
  const areaData = findArea(allAreas, id)
  const areaObj = AreaClass(id, areaData)
  const name = areaObj.getName(i18n.language)
  const description = areaObj.getDescription(i18n.language)
  const icon = areaObj.getImage(assetsUrl)
  const areaId = areaObj.getId();

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [areaId]); 

  return (
    <div 
      ref={scrollContainerRef}
      className={[style.area, y > 0 ? style.isScrolling : " "].join(" ")}
    >
      <div className={style.contentArea}>
        <AreaHeader title={name} description={description} icon={icon} step={areaData.PASO} />

        {id === "PAREDES" && <AllWallsSameControl />}

        {renderArea(areaObj)}

        {nextArea && (
          <div className={style.areabuttonNext}>
            <Button type="button" onClick={handleGoToNext}>
              {t("Next")} - {nextName}
            </Button>
          </div>
        )}
        {!nextArea && (
          <div className={style.areabuttonNext}>
            <Button type="button" onClick={handleGoToSummary}>
              {t("Go to summary")}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Area
